import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/apiClient";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Instant referrer add — same fields/endpoint as mobile /add-business.
 * Creates a LIVE unclaimed branch, enqueues admin review (source=user_added).
 */
export default function AddBusiness() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState(params.get("name") || "");
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [address, setAddress] = useState(params.get("address") || "");
  const [categoryId, setCategoryId] = useState("");
  const [cats, setCats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState(null);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data || [])).catch(() => setCats([]));
  }, []);

  const canSubmit = useMemo(() => {
    const n = name.trim();
    return n && categoryId && (phone.trim() || address.trim());
  }, [name, phone, address, categoryId]);

  const submit = async (confirmCreate = false) => {
    if (!name.trim()) return toast.error("Business name is required");
    if (!categoryId) return toast.error("Pick a category");
    if (!phone.trim() && !address.trim()) {
      return toast.error("Add a phone number or an address");
    }
    setSaving(true);
    setCandidates(null);
    try {
      const { data } = await api.post("/branches", {
        company_name: name.trim(),
        category_id: categoryId,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        confirm_create: confirmCreate,
      });
      toast.success("Business added — you can refer it now");
      nav(`/b/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "possible_duplicates") {
        setCandidates(detail.candidates || []);
        toast.message(detail.message || "Is it one of these?");
      } else {
        toast.error(apiErr(e));
      }
    } finally {
      setSaving(false);
    }
  };

  if (cats === null) return <Loading />;

  return (
    <div className="mx-auto max-w-lg px-4 py-6" data-testid="add-business-page">
      <button
        type="button"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => nav(-1)}
        data-testid="add-business-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="text-2xl font-black tracking-tight">Add a business</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Can&apos;t find them in search? Add the business so you can refer it. They&apos;ll be invited to claim their profile.
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Business name</label>
          <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Joe's Plumbing" data-testid="biz-name-input" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Category</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`biz-category-${c.id}`}
                onClick={() => setCategoryId(c.id)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  categoryId === c.id ? "bg-brand-green text-white" : "bg-secondary text-muted-foreground"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Address</label>
          <Input className="mt-1" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city" data-testid="biz-address-input" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Phone</label>
          <Input className="mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" data-testid="biz-phone-input" />
        </div>
        <p className="text-xs text-muted-foreground">Phone or address is required.</p>

        {candidates?.length ? (
          <div className="rounded-[12px] border border-brand-gold/40 bg-brand-gold/10 p-4" data-testid="dedupe-candidates">
            <p className="font-semibold">Is it one of these?</p>
            <ul className="mt-2 space-y-2">
              {candidates.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/b/${c.id}`}
                    className="block rounded-[10px] border border-border bg-card px-3 py-2 text-sm hover:border-brand-green"
                    data-testid={`dedupe-candidate-${c.id}`}
                  >
                    <span className="font-medium">{c.company_name}</span>
                    {c.address ? <span className="block text-xs text-muted-foreground">{c.address}</span> : null}
                    {c.phone ? <span className="block text-xs text-muted-foreground">{c.phone}</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
            <Button
              className="mt-3 w-full bg-brand-green hover:bg-brand-green-dark"
              disabled={saving || !canSubmit}
              onClick={() => submit(true)}
              data-testid="dedupe-add-anyway"
            >
              {saving ? "Adding…" : "None of these — add anyway"}
            </Button>
          </div>
        ) : null}

        {!candidates?.length ? (
          <Button
            className="w-full bg-brand-green hover:bg-brand-green-dark"
            disabled={saving || !canSubmit}
            onClick={() => submit(false)}
            data-testid="save-business-button"
          >
            {saving ? "Adding…" : "Add & continue to refer"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
